import React, { useCallback, useEffect, useState } from 'react';
import { BasicPageContainer } from "components/views/BasicPageContainer";
import { Box, Typography, FormControlLabel, Checkbox, TextField, Button, styled, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { usePatchWorkspaceMfa } from 'hooks/usePatchWorkspaceMfa';
import AuthGuard from 'context/auth/AuthGuard';
import { UserRoles } from 'constants/userRoles';
import { useWorkspaceInfo } from 'hooks/useWorkspaceInfo';
import { useGetWorkspaceFeedback } from 'hooks/feedback/useGetWorkspaceFeedback';
import { useCreateWorkspaceFeedback } from 'hooks/feedback/useCreateWorkspaceFeedback';
import { useUpdateWorkspaceFeedback } from 'hooks/feedback/useUpdateWorkspaceFeedback';
import { useDeleteWorkspaceFeedback } from 'hooks/feedback/useDeleteWorkspaceFeedback';
import { RewardType } from 'models/RewardType';
import { CancelButton, SaveButton } from '../profile/DisplayNameSection/DisplayNameSection.styles';

const ChangeFeedbackButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(3),
  color: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  width: '20rem',
  height: '40px'
})); 

export const WorkspaceManagementPage = () => {
    const { t } = useTranslation();
    const { workspaceInfo, loading: workspaceLoading, editingWorkspaceName, editWorkspaceName } = useWorkspaceInfo();
    const { patchMfa, patching } = usePatchWorkspaceMfa();
    const { feedbackByRewardType, feedbackLoading, feedbackRefetching, refetch } = useGetWorkspaceFeedback();
    const { createFeedback, creatingFeedback } = useCreateWorkspaceFeedback();
    const { updateFeedback, updatingFeedback } = useUpdateWorkspaceFeedback();
    const { deleteFeedback, deletingFeedback } = useDeleteWorkspaceFeedback();
    const [workspaceName, setWorkspaceName] = useState(workspaceInfo?.name || '');
    const [mfaEnabled, setMfaEnabled] = useState(workspaceInfo?.mfaEnabled || false);
    const [newWorkspaceName, setNewWorkspaceName] = useState(workspaceName);
    const [isEditing, setIsEditing] = useState(false);
    const [editingFeedbackType, setEditingFeedbackType] = useState<RewardType | null>(null);
    const [newFeedbackMessage, setNewFeedbackMessage] = useState('');

    const handleChangeMfa = useCallback(() => {
        patchMfa(!mfaEnabled)
            .then((r) => {
                if (r?.status === 200) {
                    setMfaEnabled(!mfaEnabled);
                }
            })
            .catch(error => {
                console.error('Failed to update MFA setting:', error);
            });
    }, [mfaEnabled, patchMfa]);

    const handleChangeName = useCallback(() => {
        editWorkspaceName({ name: newWorkspaceName })
            .then((r) => {
                if (r?.status === 200) {
                    setWorkspaceName(newWorkspaceName);
                    setNewWorkspaceName(newWorkspaceName);
                    setIsEditing(false);
                }
            });
    }, [editWorkspaceName, newWorkspaceName]);

    const handleSaveFeedback = useCallback(async (rewardType: RewardType) => {
        const trimmedFeedback = newFeedbackMessage.trim();
        const currentFeedback = feedbackByRewardType[rewardType];
        const currentMessage = currentFeedback?.message?.trim() ?? '';
        const closeEditor = (message: string) => {
            setNewFeedbackMessage(message);
            setEditingFeedbackType(null);
        };

        if (trimmedFeedback === currentMessage) {
            setEditingFeedbackType(null);
            return;
        }

        if (!trimmedFeedback) {
            if (currentFeedback?.id) {
                await deleteFeedback(currentFeedback.id);
                await refetch();
            }

            closeEditor('');
            return;
        }

        if (currentFeedback?.id) {
            await updateFeedback({ feedbackId: currentFeedback.id, message: trimmedFeedback });
        } else {
            await createFeedback({
                message: trimmedFeedback,
                title: null,
                lessonId: null,
                rewardType,
            });
        }

        await refetch();
        closeEditor(trimmedFeedback);
    }, [
        createFeedback,
        deleteFeedback,
        feedbackByRewardType,
        newFeedbackMessage,
        refetch,
        updateFeedback,
    ]);

    const handleEditFeedback = useCallback((rewardType: RewardType) => {
        const feedback = feedbackByRewardType[rewardType];
        setNewFeedbackMessage(feedback?.message ?? '');
        setEditingFeedbackType(rewardType);
    }, [feedbackByRewardType]);

    const handleCancelFeedbackEdit = useCallback(() => {
        setNewFeedbackMessage('');
        setEditingFeedbackType(null);
    }, []);

    useEffect(() => {
        if (workspaceInfo) {
            setWorkspaceName(workspaceInfo.name);
            setMfaEnabled(workspaceInfo.mfaEnabled);
            setNewWorkspaceName(workspaceInfo.name);
        }
    }, [workspaceInfo]);

    const feedbackPending = feedbackLoading || creatingFeedback || updatingFeedback || deletingFeedback;
    const feedbackLevels: RewardType[] = [
        RewardType.Gold,
        RewardType.Silver,
        RewardType.Bronze,
        RewardType.None,
    ];

    const getRewardTypeLabel = useCallback(
        (rewardType: RewardType) => t(`rewardType.${(RewardType[rewardType] ?? 'None').toLowerCase()}`),
        [t]
    );

    return (
        <AuthGuard requiredRoles={[UserRoles.ADMIN]}>
            <BasicPageContainer>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    justifyContent="center"
                    minHeight="60vh"
                    gap={2}
                >
                    <Typography variant="h5" mb={2}>
                        {t('mfaManagement.title', 'Multi-Factor Authentication Settings')}
                    </Typography>
                    <Typography variant="body1" mb={2} color="textSecondary" align="center">
                        {t('mfaManagement.description', 'Enable or disable multi-factor authentication for this workspace.')}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={mfaEnabled}
                                onChange={handleChangeMfa}
                                disabled={patching}
                                color="primary"
                            />
                        }
                        label={t('mfaManagement.enableMfa', 'Enable MFA')}
                    />

                    <Box mt={4} width="100%" maxWidth={400}>
                        <Typography variant="h6" mb={1}>
                            {t('workspaceManagement.workspaceName', 'Workspace Name')}
                        </Typography>
                        {isEditing ? (
                            <Box display="flex" gap={1}>
                                <TextField
                                    value={newWorkspaceName}
                                    onChange={e => setNewWorkspaceName(e.target.value)}
                                    size="small"
                                    fullWidth
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleChangeName}
                                    disabled={!newWorkspaceName.trim()}
                                >
                                    {t('general.save')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                        setNewWorkspaceName(workspaceName);
                                        setIsEditing(false);
                                    }}
                                >
                                    {t('general.cancel')}
                                </Button>
                            </Box>
                        ) : (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography>{workspaceName}</Typography>
                                <Button
                                    variant="text"
                                    onClick={() => setIsEditing(true)}
                                >
                                    {t('general.edit')}
                                </Button>
                            </Box>
                        )}
                    </Box>

                    <Box mt={4} width="100%" maxWidth={800}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="h6">
                                {t('feedbackManagement.label')}
                            </Typography>
                            {feedbackRefetching && <CircularProgress size={16} />}
                        </Box>

                        {feedbackLevels.map((rewardType) => {
                            const currentFeedback = feedbackByRewardType[rewardType];
                            const isEditingThisFeedback = editingFeedbackType === rewardType;

                            return (
                                <Box key={rewardType} mt={2}>
                                    <Typography mb={1}>
                                        {getRewardTypeLabel(rewardType)}
                                    </Typography>

                                    {isEditingThisFeedback ? (
                                        <Box display="flex" alignItems="flex-start" gap={1}>
                                            <TextField
                                                value={newFeedbackMessage}
                                                onChange={(e) => setNewFeedbackMessage(e.target.value)}
                                                size="small"
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                disabled={feedbackPending}
                                            />
                                            <Box display="flex">
                                                <SaveButton
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => handleSaveFeedback(rewardType)}
                                                    disabled={feedbackPending}
                                                >
                                                    {t('general.save')}
                                                </SaveButton>
                                                <CancelButton
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={handleCancelFeedbackEdit}
                                                    disabled={feedbackPending}
                                                >
                                                    {t('general.cancel')}
                                                </CancelButton>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box display="flex" alignItems="flex-start" gap={1}>
                                            <TextField
                                                value={currentFeedback?.message ?? ''}
                                                size="medium"
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                InputProps={{ readOnly: true }}
                                                inputProps={{ tabIndex: -1 }}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onFocus={(e) => e.currentTarget.blur()}
                                                disabled={feedbackPending}
                                            />
                                            <ChangeFeedbackButton
                                                variant="text"
                                                onClick={() => handleEditFeedback(rewardType)}
                                                disabled={feedbackPending || editingFeedbackType !== null}
                                            >
                                                {t('feedbackManagement.change')}
                                            </ChangeFeedbackButton>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </BasicPageContainer>
        </AuthGuard>
    );
};